import * as THREE from 'three';
import { Config } from '../core/Config';

/**
 * Global game state management
 * Singleton that tracks game-wide information needed by multiple systems
 */
export class GameState {
  private static instance: GameState | null = null;
  
  // Target position for toasters (the bread loaf)
  private loafPosition: THREE.Vector3 = new THREE.Vector3();
  private hasLoaf: boolean = false;
  
  // Level and wave tracking
  private currentLevelIndex: number = 0;
  private currentWaveIndex: number = 0;
  private isWaveActive: boolean = false;
  private isLevelComplete: boolean = false;
  private countdownTime: number = 0;
  private waveStartTime: number = 0;
  private waveEndTime: number = 0;
  private totalLevels: number = 0;
  
  // Economy and loaf health tracking
  private gold: number = 0;
  private loafHealth: number = 0;
  private isGameOver: boolean = false;
  private isGameWon: boolean = false;
  private gameStarted: boolean = false;
  
  // Event callbacks
  private onGameOverCallback: (() => void) | null = null;
  private onGameWinCallback: (() => void) | null = null;
  
  // Private constructor for singleton
  private constructor() {
    // Initialize economy and loaf health with default values
    this.gold = Config.ECONOMY_SYSTEM.STARTING_GOLD;
    this.loafHealth = Config.GAME_SYSTEM.STARTING_LOAF_HEALTH;
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): GameState {
    if (!GameState.instance) {
      GameState.instance = new GameState();
    }
    return GameState.instance;
  }
  
  /**
   * Set the position of the bread loaf
   */
  public setLoafPosition(position: THREE.Vector3): void {
    this.loafPosition.copy(position);
    this.hasLoaf = true;
    console.log(`Loaf position set to: ${position.x}, ${position.y}, ${position.z}`);
  }
  
  /**
   * Get the position of the bread loaf
   */
  public getLoafPosition(): THREE.Vector3 {
    return this.loafPosition.clone();
  }
  
  /**
   * Check if the loaf has been placed in the world
   */
  public isLoafPlaced(): boolean {
    return this.hasLoaf;
  }
  
  /**
   * Initialize level system with total number of levels
   */
  public initializeLevelSystem(totalLevels: number): void {
    this.totalLevels = totalLevels;
    this.currentLevelIndex = 0;
    this.currentWaveIndex = 0;
    this.isWaveActive = false;
    this.isLevelComplete = false;
  }
  
  /**
   * Start a level
   */
  public startLevel(levelIndex: number): void {
    this.currentLevelIndex = levelIndex;
    this.currentWaveIndex = 0;
    this.isLevelComplete = false;
    console.log(`Starting level ${levelIndex + 1}`);
  }
  
  /**
   * Start the next wave
   */
  public startWave(waveIndex: number): void {
    this.currentWaveIndex = waveIndex;
    this.isWaveActive = true;
    this.waveStartTime = Date.now();
    console.log(`Starting wave ${waveIndex + 1} of level ${this.currentLevelIndex + 1}`);
    console.log(`GameState wave transition - previous waveIndex: ${waveIndex > 0 ? waveIndex - 1 : 'none'}, new waveIndex: ${waveIndex}, isWaveActive: ${this.isWaveActive}`);
  }
  
  /**
   * Complete the current wave
   */
  public completeWave(): void {
    this.isWaveActive = false;
    this.waveEndTime = Date.now();
    console.log(`Completed wave ${this.currentWaveIndex + 1} of level ${this.currentLevelIndex + 1}`);
    console.log(`GameState wave status - isWaveActive: ${this.isWaveActive}, currentWaveIndex: ${this.currentWaveIndex}`);
  }
  
  /**
   * Complete the current level
   */
  public completeLevel(): void {
    this.isLevelComplete = true;
    console.log(`Completed level ${this.currentLevelIndex + 1}`);
    
    // Check if this was the final level
    if (this.currentLevelIndex >= this.totalLevels - 1) {
      // Set game won state flag
      this.isGameWon = true;
      
      // Stop game loop for toasters
      this.isWaveActive = false;
      
      console.log('All levels complete! Game won!');
      console.log('Waiting for player to click "Play Again" button...');
      
      // Release pointer lock for better UI interaction on win screen
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
      
      // Trigger game win callback if set
      if (this.onGameWinCallback) {
        this.onGameWinCallback();
      }
    }
  }
  
  /**
   * Set the countdown timer for the next wave
   */
  public setCountdownTime(seconds: number): void {
    this.countdownTime = seconds;
    console.log(`GameState: Setting countdown timer to ${seconds} seconds`);
    console.log(`GameState wave status - isWaveActive: ${this.isWaveActive}, countdownTime: ${this.countdownTime}`);
  }
  
  /**
   * Get the countdown time remaining
   */
  public getCountdownTime(): number {
    return this.countdownTime;
  }
  
  /**
   * Decrement the countdown timer
   */
  public decrementCountdown(deltaTime: number): void {
    const oldCountdown = this.countdownTime;
    this.countdownTime = Math.max(0, this.countdownTime - deltaTime);
    
    // Log when countdown crosses whole second thresholds
    if (Math.floor(oldCountdown) !== Math.floor(this.countdownTime)) {
      console.log(`GameState: Countdown at ${Math.floor(this.countdownTime)} seconds`);
      console.log(`GameState wave status - isWaveActive: ${this.isWaveActive}, currentWaveIndex: ${this.currentWaveIndex}`);
    }
  }
  
  /**
   * Check if countdown is complete
   */
  public isCountdownComplete(): boolean {
    const complete = this.countdownTime <= 0;
    if (complete) {
      console.log('GameState: Countdown complete');
    }
    return complete;
  }
  
  /**
   * Get the current level index (0-based)
   */
  public getCurrentLevelIndex(): number {
    return this.currentLevelIndex;
  }
  
  /**
   * Get the current wave index (0-based)
   */
  public getCurrentWaveIndex(): number {
    return this.currentWaveIndex;
  }
  
  /**
   * Check if a wave is currently active
   */
  public isWaveInProgress(): boolean {
    return this.isWaveActive;
  }
  
  /**
   * Check if the current level is complete
   */
  public isCurrentLevelComplete(): boolean {
    return this.isLevelComplete;
  }
  
  /**
   * Get the total number of levels
   */
  public getTotalLevels(): number {
    return this.totalLevels;
  }
  
  /**
   * Reset the game state
   */
  public reset(): void {
    this.hasLoaf = false;
    this.loafPosition.set(0, 0, 0);
    this.currentLevelIndex = 0;
    this.currentWaveIndex = 0;
    this.isWaveActive = false;
    this.isLevelComplete = false;
    this.countdownTime = 0;
    
    // Reset economy and loaf health
    this.gold = Config.ECONOMY_SYSTEM.STARTING_GOLD;
    this.loafHealth = Config.GAME_SYSTEM.STARTING_LOAF_HEALTH;
    this.isGameOver = false;
    this.isGameWon = false;
    
    console.log('GameState fully reset');
  }
  
  /**
   * Set the wave index without starting the wave
   * Used when transitioning between waves
   */
  public setWaveIndex(waveIndex: number): void {
    this.currentWaveIndex = waveIndex;
    console.log(`GameState: Set wave index to ${waveIndex + 1} without starting wave`);
    console.log(`GameState wave status - isWaveActive: ${this.isWaveActive}, currentWaveIndex: ${this.currentWaveIndex}`);
  }
  
  /**
   * Get the current gold amount
   */
  public getGold(): number {
    return this.gold;
  }
  
  /**
   * Add gold (reward for destroying toasters)
   */
  public addGold(amount: number): void {
    this.gold += amount;
    console.log(`Added ${amount} gold. New total: ${this.gold}`);
  }
  
  /**
   * Deduct gold (cost for placing towers or using gun)
   * Returns true if the gold was successfully deducted
   */
  public deductGold(amount: number): boolean {
    if (this.gold >= amount) {
      this.gold -= amount;
      console.log(`Deducted ${amount} gold. Remaining: ${this.gold}`);
      return true;
    }
    
    console.log(`Not enough gold to deduct ${amount}. Current gold: ${this.gold}`);
    return false;
  }
  
  /**
   * Check if player has enough gold for an action
   */
  public hasEnoughGold(amount: number): boolean {
    return this.gold >= amount;
  }
  
  /**
   * Get tower placement cost
   */
  public getTowerCost(): number {
    return Config.ECONOMY_SYSTEM.TOWER_COST;
  }
  
  /**
   * Get gun shot cost
   */
  public getGunCost(): number {
    return Config.ECONOMY_SYSTEM.GUN_COST_PER_SHOT;
  }
  
  /**
   * Get reward for destroying a toaster
   */
  public getToasterReward(toasterType: string): number {
    const rewards = Config.ECONOMY_SYSTEM.TOASTER_REWARDS as Record<string, number>;
    return rewards[toasterType] || 0;
  }
  
  /**
   * Get the current loaf health
   */
  public getLoafHealth(): number {
    return this.loafHealth;
  }
  
  /**
   * Damage the loaf when a toaster reaches it
   * Returns true if the damage was applied (game not over)
   */
  public damageLoaf(): boolean {
    if (this.isGameOver) return false;
    
    const damageTaken = Config.GAME_SYSTEM.LOAF_DAMAGE_PER_HIT;
    this.loafHealth = Math.max(0, this.loafHealth - damageTaken);
    console.log(`Loaf damaged! Health reduced by ${damageTaken} to ${this.loafHealth}`);
    
    // Check for game over
    if (this.loafHealth <= Config.GAME_SYSTEM.GAME_OVER_THRESHOLD) {
      this.gameOver();
      return false;
    }
    
    return true;
  }
  
  /**
   * Game over when loaf health reaches 0
   */
  private gameOver(): void {
    // Set game over state flag
    this.isGameOver = true;
    
    // Stop game loop for toasters by setting active wave to false
    this.isWaveActive = false;
    
    console.log('Game Over: Loaf has been fully consumed!');
    console.log('Waiting for player to click "Try Again" button...');
    
    // Release pointer lock for better UI interaction
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    
    // Call the game over callback if set
    if (this.onGameOverCallback) {
      this.onGameOverCallback();
    }
  }
  
  /**
   * Check if the game is over (loaf depleted)
   */
  public isGameOverState(): boolean {
    // Ensure pointer is unlocked whenever game is over
    if (this.isGameOver && document.pointerLockElement) {
      document.exitPointerLock();
    }
    return this.isGameOver;
  }
  
  /**
   * Check if the game has been won (all levels complete)
   */
  public isGameWonState(): boolean {
    // Ensure pointer is unlocked whenever game is won
    if (this.isGameWon && document.pointerLockElement) {
      document.exitPointerLock();
    }
    return this.isGameWon;
  }
  
  /**
   * Set callback for game over event
   */
  public onGameOver(callback: () => void): void {
    this.onGameOverCallback = callback;
  }
  
  /**
   * Set callback for game win event
   */
  public onGameWin(callback: () => void): void {
    this.onGameWinCallback = callback;
  }
  
  /**
   * Start the game
   */
  public startGame(): void {
    this.gameStarted = true;
    console.log('Game started!');
  }
  
  /**
   * Check if the game has been started
   */
  public isGameStarted(): boolean {
    return this.gameStarted;
  }
} 